import React from "react";
import { Box, Container, Typography, useTheme, alpha } from "@mui/material";
import { MotionBox } from "@rusha/shared-theme";
import { useThemeManager } from "../../hooks/useThemeManager";
import { JsonLd } from "../common/JsonLd";

interface MetricCard {
  label: string;
  value: string;
  dataValue: string;
  trend: string;
  trendColor: string;
  barColor: string;
  barWidth: string;
}

const METRICS: MetricCard[] = [
  {
    label: "Environments",
    value: "24",
    dataValue: "24",
    trend: "↑ 12%",
    trendColor: "#10b981",
    barColor: "#3b82f6",
    barWidth: "72%",
  },
  {
    label: "Services",
    value: "142",
    dataValue: "142",
    trend: "↑ 8%",
    trendColor: "#10b981",
    barColor: "#7c3aed",
    barWidth: "88%",
  },
  {
    label: "Deployments",
    value: "356",
    dataValue: "356",
    trend: "↑ 15%",
    trendColor: "#10b981",
    barColor: "#10b981",
    barWidth: "92%",
  },
  {
    label: "Health",
    value: "99.9%",
    dataValue: "99.9",
    trend: "Excellent",
    trendColor: "#10b981",
    barColor: "#10b981",
    barWidth: "99%",
  },
];

const SIDEBAR_ITEMS = [
  { label: "Overview", active: true },
  { label: "Templates" },
  { label: "Infrastructure" },
  { label: "Deployments" },
  { label: "Observability" },
  { label: "FinOps" },
  { label: "AI Recommendations" },
];

const RECENT_DEPLOYS = [
  { name: "checkout-api", env: "prod", envColor: "#10b981", time: "2m ago" },
  { name: "user-service", env: "staging", envColor: "#f59e0b", time: "7m ago" },
];

const HEALTH_LEGEND = [
  { color: "#10b981", label: "Healthy", count: "42" },
  { color: "#f59e0b", label: "Warning", count: "3" },
  { color: "#f43f5e", label: "Critical", count: "1" },
];

const PlatformPreview: React.FC = () => {
  const theme = useTheme();
  const { isDark } = useThemeManager();
  const mode = isDark ? "dark" : "light";

  return (
    <Box
      component="section"
      id="platform"
      aria-labelledby="platform-title"
      sx={{
        py: { xs: 8, md: 12 },
        bgcolor: isDark ? alpha("#0c0a18", 0.6) : alpha("#3b82f6", 0.02),
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        {/* Section header */}
        <MotionBox
          sx={{ textAlign: "center", mb: { xs: 4, md: 6 } }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Typography
            component="h2"
            variant="h2"
            id="platform-title"
            sx={{
              mb: 1.5,
              fontWeight: 800,
              fontSize: { xs: "1.75rem", md: "2.5rem" },
            }}
          >
            Your engineering command center
          </Typography>
          <Typography
            component="p"
            sx={{
              color: "text.secondary",
              fontSize: "1.05rem",
              maxWidth: 600,
              mx: "auto",
            }}
          >
            Everything your team needs, in one unified interface.
          </Typography>
        </MotionBox>

        {/* Dashboard mockup */}
        <MotionBox
          component="figure"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          sx={{
            m: 0,
            position: "relative",
            borderRadius: 3,
            overflow: "hidden",
            border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            boxShadow: isDark
              ? `0 20px 60px ${alpha("#000", 0.4)}`
              : `0 20px 60px ${alpha("#3b82f6", 0.1)}`,
          }}
        >
          {/* Dashboard frame background */}
          <Box
            component="img"
            src={`/assets/platform/dashboard-frame-${mode}.svg`}
            alt=""
            aria-hidden="true"
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />

          {/* Dashboard content overlay */}
          <Box
            sx={{
              position: "relative",
              display: "flex",
              minHeight: { xs: 480, md: 466 },
            }}
          >
            {/* Sidebar */}
            <Box
              component="nav"
              aria-label="Dashboard sidebar"
              sx={{
                width: 170,
                flexShrink: 0,
                p: 2,
                borderRight: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                display: { xs: "none", md: "block" },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  mb: 2.5,
                }}
              >
                <Box
                  sx={{
                    fontSize: "1.1rem",
                    fontWeight: 800,
                    color: "primary.main",
                  }}
                >
                  R
                </Box>
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    color: "text.primary",
                  }}
                >
                  rusha
                </Typography>
              </Box>
              <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
                {SIDEBAR_ITEMS.map((item) => (
                  <Box
                    component="li"
                    key={item.label}
                    sx={{
                      px: 1.5,
                      py: 1,
                      mb: 0.5,
                      borderRadius: 1,
                      fontSize: "0.8rem",
                      fontWeight: item.active ? 500 : 400,
                      color: item.active ? "primary.main" : "text.secondary",
                      bgcolor: item.active
                        ? alpha("#3b82f6", 0.08)
                        : "transparent",
                      cursor: "default",
                    }}
                  >
                    {item.label}
                  </Box>
                ))}
              </Box>
              <Box
                sx={{
                  mt: 2,
                  pt: 2,
                  borderTop: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
                  fontSize: "0.8rem",
                  color: "text.secondary",
                  px: 1.5,
                }}
              >
                Settings
              </Box>
            </Box>

            {/* Main content area */}
            <Box sx={{ flex: 1, p: { xs: 2, md: 3 } }}>
              {/* Top bar */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 3,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    component="h3"
                    sx={{
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      color: "text.primary",
                    }}
                  >
                    Overview
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: "#10b981",
                      }}
                    />
                    <Typography sx={{ fontSize: "0.7rem", color: "#10b981" }}>
                      Live
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: { xs: "none", md: "flex" },
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.75,
                      borderRadius: 1,
                      bgcolor: "background.default",
                      border: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    Search resources...
                  </Box>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      bgcolor: alpha("#7c3aed", 0.2),
                      border: `1px solid ${alpha("#7c3aed", 0.4)}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.65rem",
                      fontWeight: 600,
                      color: "#7c3aed",
                    }}
                  >
                    JD
                  </Box>
                </Box>
              </Box>

              {/* Metric cards row */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
                  gap: 1.5,
                  mb: 2,
                }}
              >
                {METRICS.map((m) => (
                  <Box
                    key={m.label}
                    component="article"
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: "background.default",
                      border: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        color: "text.secondary",
                        mb: 0.5,
                      }}
                    >
                      {m.label}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: 0.75,
                        mb: 0.75,
                      }}
                    >
                      <Typography
                        component="data"
                        value={m.dataValue}
                        sx={{
                          fontSize: "1.3rem",
                          fontWeight: 700,
                          color: "text.primary",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {m.value}
                      </Typography>
                      <Typography
                        sx={{ fontSize: "0.7rem", color: m.trendColor }}
                      >
                        {m.trend}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        height: 3,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.divider, 0.3),
                      }}
                    >
                      <Box
                        sx={{
                          height: "100%",
                          width: m.barWidth,
                          borderRadius: 2,
                          bgcolor: alpha(m.barColor, 0.5),
                        }}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Middle row: health map + recent deployments */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 1.5,
                }}
              >
                {/* Health map */}
                <Box
                  component="figure"
                  sx={{
                    m: 0,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "background.default",
                    border: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
                  }}
                >
                  <Typography
                    component="figcaption"
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      color: "text.secondary",
                      mb: 1,
                    }}
                  >
                    Infrastructure Health
                  </Typography>
                  <Box
                    component="img"
                    src="/assets/platform/health-map.svg"
                    alt=""
                    aria-hidden="true"
                    width="420"
                    height="200"
                    loading="lazy"
                    sx={{ width: "100%", height: "auto" }}
                  />
                  <Box
                    component="ul"
                    sx={{
                      listStyle: "none",
                      p: 0,
                      m: 0,
                      display: "flex",
                      gap: 2,
                      mt: 1,
                    }}
                  >
                    {HEALTH_LEGEND.map((l) => (
                      <Box
                        key={l.label}
                        component="li"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          fontSize: "0.7rem",
                          color: "text.secondary",
                          listStyle: "none",
                        }}
                      >
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            bgcolor: l.color,
                          }}
                        />
                        {l.label} {l.count}
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Right column: donut + cost chart */}
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  {/* Health donut */}
                  <Box
                    component="figure"
                    sx={{
                      m: 0,
                      p: 2,
                      borderRadius: 2,
                      bgcolor: "background.default",
                      border: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <Box
                      component="img"
                      src={`/assets/platform/health-donut-${mode}.svg`}
                      alt="Infrastructure health donut chart showing 96 percent healthy"
                      width="80"
                      height="80"
                      loading="lazy"
                    />
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          color: "text.secondary",
                          mb: 0.5,
                        }}
                      >
                        Infrastructure Health
                      </Typography>
                      <Typography
                        component="data"
                        value="96"
                        sx={{
                          fontSize: "1.5rem",
                          fontWeight: 700,
                          color: "text.primary",
                        }}
                      >
                        96%
                      </Typography>
                      <Typography
                        sx={{ fontSize: "0.7rem", color: "text.secondary" }}
                      >
                        Healthy
                      </Typography>
                    </Box>
                  </Box>

                  {/* Cost chart */}
                  <Box
                    component="figure"
                    sx={{
                      m: 0,
                      p: 2,
                      borderRadius: 2,
                      bgcolor: "background.default",
                      border: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          color: "text.secondary",
                        }}
                      >
                        Cost Optimization
                      </Typography>
                      <Typography
                        sx={{ fontSize: "0.7rem", color: "text.secondary" }}
                      >
                        This month ▾
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: 0.75,
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        component="data"
                        value="8240"
                        sx={{
                          fontSize: "1.3rem",
                          fontWeight: 700,
                          color: "text.primary",
                        }}
                      >
                        $8,240
                      </Typography>
                      <Typography
                        sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                      >
                        / month
                      </Typography>
                    </Box>
                    <Typography
                      sx={{ fontSize: "0.7rem", color: "#10b981", mb: 1 }}
                    >
                      ↓ 18% vs last month
                    </Typography>
                    <Box
                      component="img"
                      src="/assets/platform/cost-chart.svg"
                      alt=""
                      aria-hidden="true"
                      width="214"
                      height="50"
                      loading="lazy"
                      sx={{ width: "100%", height: "auto" }}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Recent deployments */}
              <Box
                sx={{
                  mt: 1.5,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "background.default",
                  border: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    color: "text.secondary",
                    mb: 1,
                  }}
                >
                  Recent Deployments
                </Typography>
                <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
                  {RECENT_DEPLOYS.map((d) => (
                    <Box
                      key={d.name}
                      component="li"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        py: 0.75,
                        listStyle: "none",
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: "#10b981",
                          boxShadow: `0 0 6px ${alpha("#10b981", 0.4)}`,
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: "0.8rem",
                          color: "text.primary",
                          flex: 1,
                        }}
                      >
                        {d.name}
                      </Typography>
                      <Box
                        sx={{
                          px: 1,
                          py: 0.25,
                          borderRadius: 0.75,
                          bgcolor: alpha(d.envColor, 0.1),
                          border: `1px solid ${alpha(d.envColor, 0.3)}`,
                          fontSize: "0.65rem",
                          color: d.envColor,
                        }}
                      >
                        {d.env}
                      </Box>
                      <Typography
                        sx={{ fontSize: "0.7rem", color: "text.secondary" }}
                      >
                        {d.time}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Annotation callouts */}
          <Box
            component="aside"
            sx={{
              position: "absolute",
              top: "8%",
              right: { xs: 2, md: 16 },
              zIndex: 5,
              maxWidth: 160,
            }}
          >
            <Box
              component="img"
              src="/assets/platform/callout-health.svg"
              alt="Real-time Health — global infrastructure status at a glance"
              width="160"
              height="50"
              loading="lazy"
              sx={{ width: "100%", height: "auto" }}
            />
          </Box>
          <Box
            component="aside"
            sx={{
              position: "absolute",
              bottom: "12%",
              left: { xs: 2, md: 16 },
              zIndex: 5,
              maxWidth: 160,
              display: { xs: "none", md: "block" },
            }}
          >
            <Box
              component="img"
              src="/assets/platform/callout-ai.svg"
              alt="AI Recommendations — auto-detects drift, waste, and security gaps"
              width="160"
              height="50"
              loading="lazy"
              sx={{ width: "100%", height: "auto" }}
            />
          </Box>
        </MotionBox>

        {/* Bottom CTA */}
        <Typography
          component="p"
          sx={{
            textAlign: "center",
            mt: { xs: 3, md: 5 },
            fontSize: "0.95rem",
            color: "text.secondary",
          }}
        >
          See it in action —{" "}
          <Typography
            component="a"
            href="https://app.rusha.tierravivaai.net"
            sx={{
              color: "primary.main",
              fontWeight: 600,
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Start your free trial
          </Typography>{" "}
          or{" "}
          <Typography
            component="a"
            href="https://rusha.io/docs"
            sx={{
              color: "text.primary",
              fontWeight: 600,
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            book a demo →
          </Typography>
        </Typography>
      </Container>

      {/* SoftwareApplication JSON-LD */}
      <JsonLd
        data={{
          "@type": "SoftwareApplication",
          name: "Rusha",
          applicationCategory: "DeveloperApplication",
          operatingSystem: "Web",
          description:
            "Engineering control plane for building, deploying, and operating applications. Unified infrastructure, GitOps, observability, FinOps, and AI recommendations.",
          url: "https://rusha.io",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          featureList: [
            "Infrastructure as code templates",
            "GitOps-powered deployment with ArgoCD",
            "Unified observability: logs, metrics, traces",
            "AI cost optimization recommendations",
            "Real-time deployment status",
            "Multi-cloud environment parity",
          ],
        }}
      />
    </Box>
  );
};

export default PlatformPreview;
